import React, { useId, useLayoutEffect, useRef } from 'react';
import { useReducedMotion } from 'framer-motion';
import { FREEZE_FOR_CAPTURE, MotionReveal } from '@components/motion/scroll-motion';
import { useWaterStory, type WaterStory } from '@components/water/water-story';
import { EnactArt, ExploreArt, MiniHouse, WishArt } from './wee-art';
import { ART, STILL_T } from './wee-poses';
import { createStoryRenderer } from './wee-render';
import { ALIVE_AT, CUE, CYCLE, SELECTION_AT, clamp01, wrap, type LoopBeat } from './wee-timeline';
import { createFrameCadence } from './frame-cadence';
import {
  Card,
  CardArt,
  CardBody,
  CardBodyInner,
  CardCopy,
  CardMini,
  CardTitle,
  Cards,
  Eyebrow,
  Heading,
  Root,
} from './wee-section.styles';

/*
 * The card copy is the mock's. Three-across it leads with the app's own
 * captions ("Improving Household Happiness", "Decide on Options with AI",
 * "Bring it to Life with AI"); stacked it uses the longer lines.
 */
/*
 * The beats' words are June's (2026-09-22): Envision became Wish and
 * Evaluate became Explore, as they did in the app; Explore's line became
 * "Let AI Find and Evaluate Options" (2026-09-23). The beats' keys
 * followed on 2026-10-28 (`wish`, `explore`, as the app's are), so the
 * `?beat=` links take the new names.
 */
const CARDS: ReadonlyArray<{
  beat: LoopBeat;
  title: string;
  sub: string;
  Art: React.FC<{ id: string }>;
}> = [
  { beat: 'wish', title: 'Wish', sub: 'Describe a Household Dream', Art: WishArt },
  { beat: 'explore', title: 'Explore', sub: 'Let AI Find and Evaluate Options', Art: ExploreArt },
  { beat: 'enact', title: 'Enact', sub: 'Bring it to Life with AI', Art: EnactArt },
];

type WeeStoryProps = {
  /** Called each time Enact finishes and the story wraps — the page's autoplay moves on then. */
  onCycleEnd?: () => void;
};

/**
 * Wish → Explore → Enact, brought to life the way the app's home
 * screen tells it: each card performs its beat on the app's clock, and
 * while the section fills the screen the page's water tells the story
 * too — deepening for the wish, colour as the options arrive, a sunrise
 * behind the house that comes alive.
 *
 * Three across, every card shows its picture and the current one lifts.
 * Stacked, a card that is not current folds to its house and its name,
 * and the current one opens wide enough for the whole picture — the
 * five options fanned out, measured and picked.
 */
export const WeeStory: React.FC<WeeStoryProps> = ({ onCycleEnd }) => {
  const reduced = useReducedMotion() ?? false;
  const still = reduced || FREEZE_FOR_CAPTURE;
  const story = useWaterStory();
  const id = `wee${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const rootRef = useRef<HTMLDivElement>(null);
  const onCycleEndRef = useRef(onCycleEnd);
  onCycleEndRef.current = onCycleEnd;

  // Layout, not a plain effect: the first frame is posed before the
  // browser paints, so a house never flashes at the SVG's origin.
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    return runStory(root, story.current, still, () => onCycleEndRef.current?.());
  }, [still, story]);

  return (
    <Root ref={rootRef}>
      <MotionReveal>
        <Eyebrow>What</Eyebrow>
        <Heading>Move Through One Challenge to the Next with AI</Heading>
      </MotionReveal>
      <Cards>
        {CARDS.map(({ beat, title, sub, Art }) => (
          <Card key={beat} data-card={beat} data-open="false">
            <CardMini>
              <MiniHouse id={`${id}${beat}`} beat={beat} />
            </CardMini>
            <CardTitle>{title}</CardTitle>
            <CardBody>
              <CardBodyInner>
                <CardArt>
                  <Art id={`${id}${beat}`} />
                </CardArt>
                <CardCopy>{sub}</CardCopy>
              </CardBodyInner>
            </CardBody>
          </Card>
        ))}
      </Cards>
    </Root>
  );
};

/** Where the story starts the first time the section settles on screen: just before Wish, so the first card lights as it arrives. */
const START_T = CUE.wish - 0.8;

/**
 * The moments `?beat=` can open the story on, as the app's deep link
 * does (`homeceo:///home?beat=`): the beats, plus the two moments inside
 * them that are events — the pick, and the house coming alive. For
 * reviewing a beat without waiting a cycle for it.
 */
const BEATS: Record<string, number> = {
  wish: CUE.wish,
  explore: CUE.explore,
  pick: SELECTION_AT - 0.4,
  enact: CUE.enact,
  alive: ALIVE_AT - 0.4,
};

function openingTime(): number {
  const beat = new URLSearchParams(window.location.search).get('beat');
  return beat !== null && beat in BEATS ? BEATS[beat] : START_T;
}

/** How many times the story has finished Enact by unwrapped time `elapsed`. */
function cyclesDone(elapsed: number): number {
  return elapsed < CUE.loop ? 0 : 1 + Math.floor((elapsed - CUE.loop) / CYCLE);
}

/** How much of the section must be on screen before the story starts, and the water's share of it ramps between these. */
const START_AT_COVERAGE = 0.5;
const WATER_FROM = 0.35;
const WATER_TO = 0.85;

function smoothstep(from: number, to: number, x: number): number {
  const p = clamp01((x - from) / (to - from));
  return p * p * (3 - 2 * p);
}

/** Starts the story on these cards; returns its teardown. */
function runStory(
  root: HTMLElement,
  story: WaterStory,
  still: boolean,
  onCycleEnd: () => void,
): () => void {
  const renderer = createStoryRenderer(root);

  if (still) {
    // One frame, just after the pick: the Explore card lifted with its
    // options measured and the amber one chosen. The water stays calm.
    renderer.render(STILL_T, false);
    story.weight = 0;
    story.focus = null;
    return () => undefined;
  }

  const cadence = createFrameCadence();
  let elapsed = openingTime();
  let started = false;
  let raf = 0;
  renderer.render(elapsed, true);

  const frame = (now: number) => {
    raf = requestAnimationFrame(frame);
    const step = cadence.tick(now);
    if (step === null) return;

    // Read layout before anything is written this frame.
    const box = root.getBoundingClientRect();
    const view = window.innerHeight;
    const coverage = clamp01(
      (Math.min(box.bottom, view) - Math.max(box.top, 0)) / Math.max(1, Math.min(box.height, view)),
    );
    const picture = renderer.enactArt?.getBoundingClientRect();

    if (!started && coverage >= START_AT_COVERAGE) started = true;
    const before = elapsed;
    if (started) elapsed += step;
    const t = wrap(elapsed);
    renderer.render(t, elapsed < CUE.loop);

    const house = renderer.enactHouse();
    story.t = t;
    story.weight = started ? smoothstep(WATER_FROM, WATER_TO, coverage) : 0;
    story.focus =
      house && picture && picture.height > 0
        ? {
            x: picture.left + (house.cx * picture.width) / ART.w,
            y: picture.top + (house.cy * picture.height) / ART.h,
          }
        : null;

    if (cyclesDone(elapsed) > cyclesDone(before)) onCycleEnd();
  };

  const stop = () => {
    cancelAnimationFrame(raf);
    raf = 0;
    story.weight = 0;
  };
  // Only runs while some of the section is on screen. Entries can arrive
  // batched; the last one is the section's current state.
  const observer = new IntersectionObserver((entries) => {
    const entry = entries[entries.length - 1];
    if (entry.isIntersecting && !raf) {
      cadence.reset();
      raf = requestAnimationFrame(frame);
    } else if (!entry.isIntersecting && raf) {
      stop();
    }
  });
  observer.observe(root);

  return () => {
    observer.disconnect();
    stop();
    story.focus = null;
  };
}
